// src/pages/profile/board-drafts/index.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import DeleteDraftButton from '../drafts/components/DeleteDraftButton';
import PopUpConfirmDeleteDraft from '../drafts/components/PopUpConfirmDeleteDraft';

import './index.css';

/**
 * Shows _all_ incomplete drafts (posts & articles) in one list.
 */
export default function ProfileDrafts({ variant = 'default' } = {}) {
  const { state } = useStateHandler();
  const userSession = state.user_session;
  const navigate    = useNavigate();
  const [activeDraftId, setActiveDraftId] = useState(null);

  const isPopupVariant = variant === 'popup';
  const POPUP_ITEM_GAP = 12;
  const BASE_ROW_HEIGHT = 56;
  const effectiveRowHeight = isPopupVariant ? BASE_ROW_HEIGHT + POPUP_ITEM_GAP : BASE_ROW_HEIGHT;

  // 1) Fetch all incomplete drafts
  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${userSession._id}_all_drafts`,
    // always include sort on createdAt:
    `${config.apis.api.url}/posts/status/incomplete?sort=-createdAt`,
    {
      injectToken: true,
      invalidateWhen: ['POST_DRAFT_SAVED', 'ARTICLE_DRAFT_SAVED', 'SUBMIT_POST', `${userSession._id}_REFRESH`]
    }
  );

  // 2) Remove a draft locally + remote
  function removeDraft(draftId) {
    setFetchData(prev => ({
      ...prev,
      totalDocs: prev.totalDocs - 1,
      data: prev.data.filter(d => d._id !== draftId)
    }));
    GalleryService.removePostDraft(draftId);
  }

  // ─── AUTO-LOAD EVERY PAGE ─────────────────────────────────────
  // as soon as the previous page arrives, if there are more, fetch the next
  useEffect(() => {
    if (
      !loading &&                                // last page finished
     fetchData?.data &&                         // we have some data
     fetchData.data.length < fetchData.totalDocs // and it’s not "all pages yet"
    ) {
      loadMoreData();
    }
  }, [loading, fetchData?.data?.length, fetchData?.totalDocs, loadMoreData]);
  // ───────────────────────────────────────────────────────────────

  // only show the full‐screen loader if *no* drafts have arrived yet
  const initialLoad = loading && (!fetchData?.data || fetchData.data.length === 0);
  if (initialLoad) {

    // only show the full‐screen loader until *all* pages have loaded. THIS MAKES THE LOADER WORK LAGGED (JORGE). To try just comment the two lines on top and uncomment the lines below.
    //   const fullyLoaded =
    //     fetchData?.data &&
    //     fetchData.data.length >= fetchData.totalDocs &&
    //     !loading;
    //   if (!fullyLoaded) {

    return (
      <div className="post-drafts__container post-drafts__loading-box">
        <Loader />
      </div>
    );
  }

  const draftsOnly = fetchData?.data
    ? fetchData.data.filter(item => {
      const status = (
        item.post_status ??
          item.status ??
          item.draft_status ??
          item.post_container?.post_status ??
          item.post_container?.draft_status ??
          ''
      ).toString().toLowerCase();
      return status === 'incomplete';
    })
    : [];

  if (!loading && draftsOnly.length === 0) {
    return (
      <div className="post-drafts__container">
        <Text className="post-drafts__no-drafts font-size-medium" paragraph>
          You don’t have any saved drafts right now.
        </Text>
      </div>
    );
  }
  const totalLoaded = fetchData?.data?.length ?? 0;

  return (
    <div className="post-drafts__container">
      <InfiniteScroll
        className="post-tags-virtual-list__container remove-scrollbar"
        onLoadMore={loadMoreData}
        shouldLoadMore={totalLoaded < (fetchData?.totalDocs || 0)}
        offsetElements={10}
        listLength={draftsOnly.length}
        loader={<Loader />}  
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => {
                  /* ———————————————————————————————————————
                     ① newest-first sort (updatedAt ▸ createdAt)
                   ——————————————————————————————————————— */
                  // always re-sort globally by createdAt desc
                  const draftsSorted = [...draftsOnly].sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                  );
                  return (
                    <List
                      autoHeight
                      width={width || 0.1}
                      height={height * draftsSorted.length - 0.001}   /* ② */
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      rowCount={draftsSorted.length} /* ③ */
                      rowHeight={effectiveRowHeight}
                      scrollTop={scrollTop}
                      overscanRowCount={10}
                      rowRenderer={({ index, key, style }) => {
                        const draft = draftsSorted[index];             /* ④ */
                        const isArticle   = Boolean(draft.post_container.post_title?.trim());
                        const routeBase   = isArticle ? '/profile/edit-article/' : '/profile/edit-post/';
                        const title       = isArticle
                          ? draft.post_container.post_title
                          : `Post #${draft._id.slice(-6)}`;

                        // ── TEXT SNIPPET LOGIC (or fallback to block type) ──

                        // 1) Grab first text-block but *skip* if it's purely atomic markup
                        const textBlocks = draft.post_texts?.length
                          ? draft.post_texts
                          : draft.post_container?.post_texts || [];
                        let snippet = '';
                        if (textBlocks.length) {
                          const first = textBlocks[0].content || '';
                          // if it's the atomic wrapper for a poll/quiz, skip it
                          if (!first.includes('data-atomic="true"')) {
                            // strip any remaining tags/br/&nbsp;/empty-divs
                            let raw = first
                              .replace(/<br\s*\/?>/gi, '')
                              .replace(/&nbsp;/gi, '')
                              .replace(/<div><\/div>/gi, '')
                              .trim();
                            if (raw) {
                              const tmp = document.createElement('div');
                              tmp.innerHTML = raw;
                              const plain = tmp.textContent || '';
                              const maxLen = 30;
                              snippet = plain.length > maxLen
                                ? plain.slice(0, maxLen - 1) + '…'
                                : plain;
                            }
                          }
                        }

                        // 2) If we got nothing (or it was atomic-only), fall back to structure
                        if (!snippet) {
                          const struct = draft.post_structure
                                        ?? draft.post_container?.post_structure
                                        ?? [];
                          const blk = struct.find(b => b.type !== 'post_texts');
                          if (blk?.type === 'post_polls')    snippet = 'Poll';
                          else if (blk?.type === 'post_quizzes') snippet = 'Quiz';
                          else if (blk?.type === 'post_pictures') snippet = 'Image';
                          else if (blk?.type === 'post_artworks') snippet = 'Artwork';
                        }

                        // 3) Still empty? fall back to title
                        if (!snippet) snippet = title;
                        // ────────────────────────

                        const rowStyle = isPopupVariant
                          ? { ...style, height: effectiveRowHeight }
                          : style;
                        const isLastRow = index === draftsSorted.length - 1;

                        const draftCard = (
                          <div
                            className="post-drafts__draft element-non-selectable"
                            onClick={() => navigate(routeBase + draft._id, {
                              state: { postData: draft }
                            })}
                          >
                            <DeleteDraftButton
                              onClick={e => {
                                e.stopPropagation();
                                setActiveDraftId(draft._id);
                              }}
                            />

                            {/* confirm-delete popup */}
                            {activeDraftId === draft._id && (
                              <PopUpConfirmDeleteDraft
                                open
                                close={() => setActiveDraftId(null)}
                                draftName={title}
                                onConfirm={e => {
                                  e.stopPropagation();
                                  removeDraft(draft._id);
                                }}
                              />
                            )}

                            {/* title/snippet on the left */}
                            <Text className="post-drafts__draft-title dots-on-overflow font-size-medium">
                              {snippet || title}
                            </Text>

                            {/* badge + arrow container */}
                            <div
                              className="post-drafts__draft-info"
                            >
                              <Text className="post-drafts__draft-type font-size-small">
                                {isArticle ? 'ARTICLE' : 'POST'}
                              </Text>
                              <ForwardArrowIcon className="post-drafts__enter-draft" />
                            </div>
                          </div>
                        );

                        return (
                          <div
                            key={key}
                            style={rowStyle}
                            ref={infiniteScrollAssigner(index)}
                          >
                            {isPopupVariant ? (
                              <div className="pop-up-drafts__item-wrapper">
                                {draftCard}
                                {!isLastRow && <div className="pop-up-drafts__item-spacer" />}
                              </div>
                            ) : (
                              draftCard
                            )}
                          </div>
                        );
                      }}
                    />
                  );               //  ← right here!
                }}         
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </div>
  );
}
